import { isEqual } from 'lodash';
import style from '../styles/keyboard.module.scss';

/**
 * Pure functions for computing key class names and widths.
 * No hooks, no React imports.
 */

const TEXT_TYPES = new Set(['text', 'textWithOutDone', 'emailText']);
const EMAIL_LIKE_TYPES = new Set(['textWithoutNumeric', 'textWithNumericEmail', 'textDefaultCaps']);
const NUMERIC_TYPES = new Set(['number', 'numericAlpha', 'numeric', 'swipe']);

/**
 * Get the base CSS class for each key button.
 * Replaces the outer cn(...) in ReturnKeyboard.
 */
export function getKeyBaseClass(type, screenType) {
  if (TEXT_TYPES.has(type)) return style.keyStylesAlpha;
  if (
    type === 'numericAlpha' ||
    screenType === 'loyalty' ||
    screenType === 'payAnAmount' ||
    screenType === 'cashCalculator' ||
    screenType === 'otherAmount' ||
    screenType === 'receiptPopUp' ||
    screenType === 'checkCreate' ||
    screenType === 'otherOrder' ||
    screenType === 'textreceiptPopUp' ||
    screenType === 'voidPopUp' ||
    screenType === 'QrSendPopUp' ||
    screenType === 'openDollarPopUp'
  ) {
    return style.keyStyleLoyalty;
  }
  return style.keyStyles;
}

/**
 * Compute the size/font class for a key (replaces returnStyle).
 *
 * @param {string}  key
 * @param {string}  type
 * @param {string}  screenType
 * @param {boolean} tableKeyHeight
 * @param {boolean} isDiscount
 * @param {boolean} employeeCheck
 * @param {boolean} keyboardHeight
 * @param {boolean} isCounter
 * @param {boolean} isDriveThru
 * @param {boolean} isCallCenter
 * @param {string}  spacebtn
 */
export function getKeyStyle(
  key,
  type,
  screenType,
  tableKeyHeight,
  isDiscount,
  employeeCheck,
  keyboardHeight,
  isCounter,
  isDriveThru,
  isCallCenter,
  spacebtn,
  donebuttontheme,
) {
  if (TEXT_TYPES.has(type)) {
    switch (key) {
      case '{space}':
        return `${spacebtn || ''} ${
          !isCounter || !isDriveThru || !isCallCenter
            ? screenType === 'couponPopUp'
              ? ''
              : style.spaceBtnHeightP
            : style.spaceBtnHeight
        }`;
      case '{done}':
      case '{continue}':
        return style.doneBtn;
      case '{shift}':
        return 'px-3';
      case '{bksp}':
        return 'px-3';
      default:
        return 'w-12';
    }
  }

  // Non-text types
  switch (key) {
    case '{done}':
    case '{continue}': {
      if (EMAIL_LIKE_TYPES.has(type)) {
        return `${style.doneBtnEmail} ${style.doneBtnFlexBasis} ${
          donebuttontheme && type === 'textDefaultCaps' ? 'od-primary-btn-bg-color od-primary-btn-font-color' : ''
        }`;
      }
      if (
        screenType === 'otherOrder' ||
        (screenType === 'receiptPopUp' && (!isCounter || !isDriveThru || !isCallCenter))
      ) {
        return tableKeyHeight ? style.numKeyPadbtnsizelandscape : style.numKeyPadbtnsizePortrait;
      }
      return style.doneBtn;
    }

    case '{shift}':
      return `col-2 ${screenType === 'receiptPopUp' ? style.textbtnsizeEmailRecieptScreen : style.textbtnsize}`;

    case '{bksp}': {
      if (EMAIL_LIKE_TYPES.has(type)) {
        return `col-2 ${screenType === 'receiptPopUp' ? style.textbtnsizeEmailRecieptScreen : style.textbtnsize}`;
      }
      if (type === 'numericAlpha') return style.textbtnsize;
      if (screenType === 'loginContainer') return `${style.numbtnsize} ${employeeCheck ? 'lh_18' : ''}`;
      if (screenType === 'AddSeats') return style.numKeyPadbtnsizelandscape;
      if (screenType === 'cashCalculator') return style.cashCalculatorKeyBtn;
      if (screenType === 'cashOutPotrait') return style.cashOutPotrait;
      if (screenType === 'cashOutLandScape') return style.cashOutLandScape;
      if (isCounter) return isDiscount ? style.numKeyPadbtnsizePortrait : style.numKeyPadbtnsize;
      return tableKeyHeight
        ? style.numKeyPadbtnsizelandscape
        : keyboardHeight
        ? style.numKeyPadbtnsizetiplandscape
        : style.numKeyPadbtnsizePortrait;
    }

    case '{space}': {
      if (EMAIL_LIKE_TYPES.has(type) || type === 'numericAlpha') {
        return `col-5 ${screenType === 'receiptPopUp' ? style.textbtnsizeEmailRecieptScreen : style.textbtnsize}`;
      }
      return '';
    }

    case '.com': {
      if (EMAIL_LIKE_TYPES.has(type)) {
        return `${
          screenType === 'receiptPopUp' ? style.textbtnsizeEmailRecieptScreen : style.doneBtnEmail
        } ${style.doneBtnFlexBasis}`;
      }
      if (
        screenType === 'otherOrder' ||
        (screenType === 'receiptPopUp' && (!isCounter || !isDriveThru || !isCallCenter))
      ) {
        return tableKeyHeight ? style.numKeyPadbtnsizelandscape : style.numKeyPadbtnsizePortrait;
      }
      return style.doneBtn;
    }

    case '{default}':
    case '{lowercase}':
      return screenType === 'receiptPopUp' ? style.textbtnsizeEmailRecieptScreen : style.textbtnsize;

    default: {
      if (EMAIL_LIKE_TYPES.has(type) || type === 'numericAlpha') {
        return screenType === 'receiptPopUp' ? style.textbtnsizeEmailRecieptScreen : style.textbtnsize;
      }
      if (screenType === 'loginContainer') return `${style.numbtnsize} ${employeeCheck ? 'lh_18' : ''}`;
      if (screenType === 'AddSeats') return style.numKeyPadbtnsizelandscape;
      if (screenType === 'cashCalculator') return style.cashCalculatorKeyBtn;
      if (screenType === 'cashOutPotrait') return style.cashOutPotrait;
      if (screenType === 'cashOutLandScape') return style.cashOutLandScape;
      if (isCounter) return isDiscount ? style.numKeyPadbtnsizePortrait : style.numKeyPadbtnsize;
      return tableKeyHeight
        ? style.numKeyPadbtnsizelandscape
        : keyboardHeight
        ? style.numKeyPadbtnsizetiplandscape
        : style.numKeyPadbtnsizePortrait;
    }
  }
}

/**
 * Compute the width class for a key (replaces returnWidth).
 *
 * @param {string}  key
 * @param {Array}   currentLayout
 * @param {object}  allLayouts
 * @param {boolean} isCounter
 * @param {boolean} isDriveThru
 * @param {boolean} isCallCenter
 * @param {string}  type
 */
export function getKeyWidth(key, currentLayout, allLayouts, isCounter, isDriveThru, isCallCenter, type) {
  const { emailText, textWithoutNumeric, text, textWithOutDone } = allLayouts;

  switch (key) {
    case '{space}': {
      const isEmailLayout =
        isEqual(currentLayout, emailText.default) || isEqual(currentLayout, emailText.shift);
      const isTextWithoutNumericShift = isEqual(currentLayout, textWithoutNumeric.shift);

      if ((isEmailLayout || isTextWithoutNumericShift) && (!isCounter || !isDriveThru || !isCallCenter)) {
        return 'w-100 col-5';
      }
      if (isEmailLayout && (isCounter || isDriveThru || isCallCenter)) {
        return 'w-100 col-8';
      }
      if (isEqual(currentLayout, text.alt) || isEqual(currentLayout, textWithOutDone.alt)) {
        return 'w-100 col-7';
      }
      const isTextDefault =
        isEqual(currentLayout, text.default) ||
        isEqual(currentLayout, textWithOutDone.default) ||
        isEqual(currentLayout, text.shift) ||
        isEqual(currentLayout, textWithOutDone.shift);
      if (isTextDefault && (!isCounter || !isDriveThru || !isCallCenter)) {
        return 'col-8';
      }
      return 'w-100 col-5';
    }

    case '{alt}':      return 'w-25 col-1 overflow-hidden';
    case '{default}':  return 'w-25';
    case '{lowercase}':return 'w-25';
    case '@':          return 'w-15';

    case '{done}':
    case '{continue}':
      return NUMERIC_TYPES.has(type) ? 'w-100' : 'w-20';

    case '{shift}':    return 'col-1';
    case '{special}':  return 'col-2';

    default:           return 'w-12';
  }
}
